import { useContext } from "react";
import { PageStateContext } from "../PageContext";
import { debugLog } from "../utils";

/**
 * Custom hook to evaluate rendering conditions for content items.
 *
 * @returns {Object} - Contains the `evaluateConditions` function.
 */
const useConditionEvaluator = () => {
  const { pageState } = useContext(PageStateContext);
  /**
   * Evaluates the conditions for an item and determines if it should be hidden.
   *
   * @param {Object} item - The content item or block to evaluate.
   * @returns {Object} - Contains:
   *   - `shouldHide`: boolean indicating if the item should be hidden.
   *   - `conditionSummary`: string summarizing the active restrictive conditions.
   */
  const evaluateConditions = item => {
    if (!item || typeof item !== "object") {
      return {
        shouldHide: false,
        conditionSummary: "None (no item or invalid item shape)",
      };
    }

    const {
      showForThisSiteLanguage = null,
      showForTheFollowingProducts = null,
      showForTheFollowingProductCategories = null,
      showForTheFollowingProductFeatures = null,
    } = item;

    // Initialize flags
    let shouldHide = false; // Default to not hiding
    const activeRestrictiveConditions = [];

    // Check site language condition (only if set to false)
    if (typeof showForThisSiteLanguage === "boolean") {
      if (!showForThisSiteLanguage) {
        shouldHide = true;
        activeRestrictiveConditions.push("Hidden for this site language");
      }
      // If true, do not consider it as an active condition
    }

    // Check for products condition
    if (
      Array.isArray(showForTheFollowingProducts) &&
      showForTheFollowingProducts.length > 0
    ) {
      if (pageState.manualId) {
        const parentProductId = pageState.manualId; // Assuming `manualId` is the parent product ID
        const isProductMatch = showForTheFollowingProducts.some(
          product => product.id === parentProductId
        );
        if (!isProductMatch) {
          shouldHide = true;
          const products = showForTheFollowingProducts
            .map(prod => prod.title)
            .join(", ");
          activeRestrictiveConditions.push(`Hidden for not matching products: ${products}`);
        }
      } else {
        // manualId is missing; cannot evaluate conditions fully
        activeRestrictiveConditions.push(
          "Conditions set for products but no manualId set. Most likely this is because this entry or block is being viewed outside of a product. You will need to preview the full product manual to populate this value and hide this content."
        );
        debugLog(
          "condition handler: manualId missing from pageState:",
          pageState
        );
      }
    }

    // Check for product categories condition
    if (
      Array.isArray(showForTheFollowingProductCategories) &&
      showForTheFollowingProductCategories.length > 0
    ) {
      if (pageState.productCategory && pageState.productCategory.length > 0) {
        const manualProductCategories = pageState.productCategory.map(
          cat => cat.id
        );
        const isCategoryMatch = showForTheFollowingProductCategories.some((cat) =>
          manualProductCategories.includes(cat.id)
        );
        if (!isCategoryMatch) {
          shouldHide = true;
          const categories = showForTheFollowingProductCategories
            .map(cat => cat.title || cat.id) // Prefer title if available
            .join(", ");
          activeRestrictiveConditions.push(
            `Hidden for not matching product categories: ${categories}`
          );
        }
      } else {
        // productCategory is missing; cannot evaluate conditions fully
        activeRestrictiveConditions.push(
          "Conditions set for product categories but no productCategory items added to this product, or a product manual is not currently being previewed"
        );
      }
    }

    // Check for product features condition
    if (
      Array.isArray(showForTheFollowingProductFeatures) &&
      showForTheFollowingProductFeatures.length > 0
    ) {
      if (pageState.productFeatures && pageState.productFeatures.length > 0) {
        const manualProductFeatures = pageState.productFeatures.map(
          feature => feature.id
        );
        const isFeatureMatch = showForTheFollowingProductFeatures.some(feat =>
          manualProductFeatures.includes(feat.id)
        );
        if (!isFeatureMatch) {
          shouldHide = true;
          const features = showForTheFollowingProductFeatures
            .map(feature => feature.title || feature.id) // Prefer title if available
            .join(", ");
          activeRestrictiveConditions.push(
            `Hidden for not matching product features: ${features}`
          );
        }
      } else {
        // productFeatures is missing; cannot evaluate conditions fully
        activeRestrictiveConditions.push(
          "Conditions set for product features but no productFeatures added to this product, or a product manual is not currently being previewed"
        );
      }
    }
    // Generate condition summary for tooltips and logging
    const conditionSummary =
      activeRestrictiveConditions.length > 0
        ? activeRestrictiveConditions.join("; ")
        : "None";

    // Determine if shouldHide based on evaluable conditions
    // shouldHide remains true only if conditions are set and evaluate to hide
    // If conditions are set but not fully evaluable, do not hide
    const hasEvaluableHideConditions = activeRestrictiveConditions.some((cond) =>
      cond.startsWith("Hidden for")
    );

    shouldHide = hasEvaluableHideConditions ? shouldHide : false;

    return {
      shouldHide,
      conditionSummary,
      hasEvaluableHideConditions,
      activeRestrictiveConditions,
    };
  };

  return { evaluateConditions };
};

export default useConditionEvaluator;
